import { useEffect, useId, useState, type InputHTMLAttributes } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import type { Resource, ResourceDetail } from '@bekebi/contracts/api';
import { IDENTIFIER_PATTERN } from '@bekebi/domain';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useServices } from '../services/api';
import { useAllResources, useCreateBinding, useCreateResource, useUpdateResource } from './api';

/**
 * Infrastructure forms — exactly the fields the API accepts. There are no credential, password,
 * key or token fields: the inventory never holds secrets. A resource's kind is a short identifier
 * (the domain deliberately fixes no list); kinds and roles already recorded are offered as
 * suggestions, nothing is invented. The API validates everything again.
 */

const IDENTIFIER_MESSAGE =
  'Use lowercase letters, digits and underscores, starting with a letter (up to 63 characters).';
const identifier = z.string().trim().regex(IDENTIFIER_PATTERN, IDENTIFIER_MESSAGE);
const name = z
  .string()
  .trim()
  .min(1, 'Enter a name.')
  .max(200, 'The name can be at most 200 characters.');
const optionalText = (max: number) => z.string().trim().max(max, `At most ${max} characters.`);

function Field({
  id,
  label,
  hint,
  error,
  inputProps,
  suggestions,
}: {
  id: string;
  label: string;
  hint?: string;
  error: string | undefined;
  inputProps: InputHTMLAttributes<HTMLInputElement> & UseFormRegisterReturn;
  suggestions?: string[];
}) {
  const listId = `${id}-suggestions`;
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-fg-2">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy || undefined}
        list={suggestions && suggestions.length > 0 ? listId : undefined}
        autoComplete="off"
        className="mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        {...inputProps}
      />
      {suggestions && suggestions.length > 0 ? (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      ) : null}
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ParentSelect({
  id,
  resources,
  excludeId,
  registerProps,
}: {
  id: string;
  resources: Resource[];
  excludeId?: string;
  registerProps: UseFormRegisterReturn;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-fg-2">
        Part of (optional)
      </label>
      <select
        id={id}
        className="mt-1 block min-h-10 w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 text-sm"
        {...registerProps}
      >
        <option value="">Not part of another resource</option>
        {resources
          .filter((r) => r.id !== excludeId)
          .map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.kind})
            </option>
          ))}
      </select>
    </div>
  );
}

const distinct = (values: string[]) => [...new Set(values)].sort();

// ---------------------------------------------------------------------------------------------
// Add resource (/app/infrastructure/new)
// ---------------------------------------------------------------------------------------------

const createSchema = z.object({
  kind: identifier,
  name,
  externalRef: optionalText(500),
  parentResourceId: z.string(),
});
type CreateFields = z.infer<typeof createSchema>;

export function ResourceCreatePage() {
  const resources = useAllResources(true);
  const create = useCreateResource();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateFields>({
    resolver: zodResolver(createSchema),
    defaultValues: { kind: '', name: '', externalRef: '', parentResourceId: '' },
  });

  const onSubmit = handleSubmit((v) =>
    create.mutate(
      {
        kind: v.kind,
        name: v.name,
        externalRef: v.externalRef === '' ? null : v.externalRef,
        parentResourceId: v.parentResourceId === '' ? null : v.parentResourceId,
      },
      {
        onSuccess: (resource) =>
          void navigate(`/app/infrastructure/${resource.id}`, {
            state: { notice: 'The resource was added.' },
          }),
      },
    ),
  );

  if (resources.isPending) return <LoadingState label="Loading resources…" />;
  if (resources.isError) {
    return (
      <ErrorState
        title="Resources could not be loaded"
        message={resources.error.message}
        onRetry={() => void resources.refetch()}
      />
    );
  }

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/infrastructure"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Infrastructure
        </Link>
      </p>
      <h1 className="mt-2">Add resource</h1>
      <p className="mt-1 text-sm text-muted">
        Record a system Bekebi&rsquo;s services depend on. Only names and references are stored —
        never sign-in details of any kind.
      </p>
      <form onSubmit={onSubmit} noValidate aria-label="Add resource" className="mt-6 space-y-5">
        <Field
          id="resource-kind"
          label="Kind"
          hint={`${IDENTIFIER_MESSAGE} Kinds already recorded are suggested.`}
          error={errors.kind?.message}
          inputProps={{ ...register('kind'), spellCheck: false }}
          suggestions={distinct(resources.data.map((r) => r.kind))}
        />
        <Field
          id="resource-name"
          label="Name"
          error={errors.name?.message}
          inputProps={register('name')}
        />
        <Field
          id="resource-external-ref"
          label="External reference (optional)"
          hint="The provider's own identifier for this resource — an identifier, never sign-in details."
          error={errors.externalRef?.message}
          inputProps={{ ...register('externalRef'), spellCheck: false }}
        />
        <ParentSelect
          id="resource-parent"
          resources={resources.data}
          registerProps={register('parentResourceId')}
        />
        {create.isError ? (
          <p role="alert" className="text-sm text-danger">
            {create.error.message}
          </p>
        ) : null}
        <div className="flex gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add resource'}
          </Button>
          <Link
            to="/app/infrastructure"
            className="inline-flex min-h-10 items-center rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-raised"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Edit resource (name, external reference, parent; the kind cannot change)
// ---------------------------------------------------------------------------------------------

const editSchema = z.object({ name, externalRef: optionalText(500), parentResourceId: z.string() });
type EditFields = z.infer<typeof editSchema>;

const editValues = (r: ResourceDetail): EditFields => ({
  name: r.name,
  externalRef: r.externalRef ?? '',
  parentResourceId: r.parentResourceId ?? '',
});

export function EditResourceForm({ resource }: { resource: ResourceDetail }) {
  const resources = useAllResources(true);
  const update = useUpdateResource(resource.id);
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFields>({
    resolver: zodResolver(editSchema),
    defaultValues: editValues(resource),
  });

  // Show the API's values when a new version arrives, without re-mounting (keeps the confirmation).
  const version = resource.updatedAt;
  useEffect(() => {
    reset(editValues(resource));
  }, [version]);

  const onSubmit = handleSubmit((v) => {
    setSaved(false);
    const current = editValues(resource);
    const patch: Record<string, string | null> = {};
    if (v.name !== current.name) patch.name = v.name;
    if (v.externalRef !== current.externalRef) patch.externalRef = v.externalRef || null;
    if (v.parentResourceId !== current.parentResourceId) {
      patch.parentResourceId = v.parentResourceId || null;
    }
    if (Object.keys(patch).length === 0) {
      setSaved(true);
      return;
    }
    update.mutate(patch, { onSuccess: () => setSaved(true) });
  });

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Edit resource" className="space-y-4">
      <Field
        id="edit-resource-name"
        label="Name"
        error={errors.name?.message}
        inputProps={register('name')}
      />
      <Field
        id="edit-resource-external-ref"
        label="External reference (optional)"
        hint="The provider's own identifier for this resource — an identifier, never sign-in details."
        error={errors.externalRef?.message}
        inputProps={{ ...register('externalRef'), spellCheck: false }}
      />
      {resources.data ? (
        <ParentSelect
          id="edit-resource-parent"
          resources={resources.data}
          excludeId={resource.id}
          registerProps={register('parentResourceId')}
        />
      ) : null}
      {update.isError ? (
        <p role="alert" className="text-sm text-danger">
          {update.error.message}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="text-sm text-success">
          The resource was saved.
        </p>
      ) : null}
      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// Bind a service to this resource — POST /api/services/:serviceId/bindings
// ---------------------------------------------------------------------------------------------

const bindSchema = z.object({
  serviceId: z.string().min(1, 'Choose a service.'),
  role: identifier,
  scopeRef: optionalText(500),
});
type BindFields = z.infer<typeof bindSchema>;

export function BindServiceForm({ resource }: { resource: ResourceDetail }) {
  const services = useServices();
  const bind = useCreateBinding();
  const [done, setDone] = useState<string | null>(null);
  const formId = useId();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BindFields>({
    resolver: zodResolver(bindSchema),
    defaultValues: { serviceId: '', role: '', scopeRef: '' },
  });

  const onSubmit = handleSubmit((v) => {
    setDone(null);
    bind.mutate(
      {
        serviceId: v.serviceId,
        resourceId: resource.id,
        role: v.role,
        ...(v.scopeRef ? { scopeRef: v.scopeRef } : {}),
      },
      {
        onSuccess: () => {
          const service = services.data?.find((s) => s.id === v.serviceId);
          setDone(`${service?.name ?? 'The service'} is now bound to this resource.`);
          reset();
        },
      },
    );
  });

  if (services.isPending) return <LoadingState label="Loading services…" />;
  if (services.isError) {
    return (
      <ErrorState
        title="Services could not be loaded"
        message={services.error.message}
        onRetry={() => void services.refetch()}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Bind a service" className="space-y-4">
      <div>
        <label htmlFor={`${formId}-service`} className="block text-sm font-medium text-fg-2">
          Service
        </label>
        <select
          id={`${formId}-service`}
          className="mt-1 block min-h-10 w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 text-sm"
          {...register('serviceId')}
        >
          <option value="">Choose a service…</option>
          {services.data.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.type === 'website' && s.accessUrl ? ` (${s.accessUrl})` : ''}
            </option>
          ))}
        </select>
        {errors.serviceId ? (
          <p className="mt-1 text-sm text-danger">{errors.serviceId.message}</p>
        ) : null}
      </div>
      <Field
        id={`${formId}-role`}
        label="Role"
        hint={`What this resource does for the service. ${IDENTIFIER_MESSAGE}`}
        error={errors.role?.message}
        inputProps={{ ...register('role'), spellCheck: false }}
        suggestions={distinct(resource.linkedServices.map((l) => l.role))}
      />
      <Field
        id={`${formId}-scope`}
        label="Scope (optional)"
        hint="Which part of the resource the service uses, if not all of it."
        error={errors.scopeRef?.message}
        inputProps={{ ...register('scopeRef'), spellCheck: false }}
      />
      {bind.isError ? (
        <p role="alert" className="text-sm text-danger">
          {bind.error.message}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="text-sm text-success">
          {done}
        </p>
      ) : null}
      <Button type="submit" disabled={bind.isPending}>
        {bind.isPending ? 'Binding…' : 'Bind service'}
      </Button>
    </form>
  );
}
